/**
 * llm-content-fixes.ts
 *
 * Client-side port of the auto-fix logic from chef/app/lib/.server/prototyper/tools.ts
 * and auto-import.ts. Applied in executeWriteFile so the streaming (client-side tools)
 * path gets the same corrections the server-side non-streaming path has always had.
 *
 * Ported from Chef — keep allowlists (LUCIDE_ICONS, SHADCN_COMPONENTS) in sync manually.
 */

// ── HTML entity decoding ──────────────────────────────────────────────────────

const HTML_ENTITIES: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&amp;': '&',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&#x2F;': '/',
  '&#x60;': '`',
  '&#x3D;': '=',
};
const ENTITY_REGEX = new RegExp(Object.keys(HTML_ENTITIES).join('|'), 'g');

/** Only decode when the payload has no raw markup at all, i.e. the model HTML-escaped the file. */
function decodeHtmlEntities(str: string): string {
  if (str.includes('<')) {
    return str;
  }
  return str.replace(ENTITY_REGEX, (match) => HTML_ENTITIES[match]!);
}

/**
 * Some models emit an entire file as a single JSON-escaped line. Repair only that
 * case: a payload with real newlines is already source code, and its `\n` sequences
 * are valid escapes inside string literals that must not be rewritten.
 */
function repairFullyEscapedPayload(content: string): string {
  if (/\r|\n/.test(content)) {
    return content;
  }
  const unescaped = content
    .replace(/(?<!\\)\\n/g, '\n')
    .replace(/(?<!\\)\\t/g, '\t')
    .replace(/(?<!\\)\\r/g, '\r');
  return unescaped.split('\n').length >= 3 ? unescaped : content;
}

// ── Template-literal backtick fix ─────────────────────────────────────────────
// Some models emit `={someVar}` where a template literal is required.
// e.g. `className={`${base} extra`}` → should be `className={\`${base} extra\`}`.

function fixMissingTemplateLiterals(content: string): string {
  let result = '';
  let i = 0;

  while (i < content.length) {
    if (
      i + 2 < content.length &&
      content[i] === '=' &&
      content[i + 1] === '{' &&
      /[a-zA-Z]/.test(content[i + 2]!)
    ) {
      let depth = 1;
      let j = i + 2;
      let hasDollarBrace = false;

      while (j < content.length && depth > 0) {
        const ch = content[j]!;
        if (ch === "'" || ch === '"' || ch === '`') {
          const quote = ch;
          j++;
          while (j < content.length && content[j] !== quote) {
            if (content[j] === '\\') j++;
            j++;
          }
          j++;
          continue;
        }
        if (ch === '{') depth++;
        else if (ch === '}') {
          depth--;
          if (depth === 0) break;
        }
        if (ch === '$' && j + 1 < content.length && content[j + 1] === '{') {
          hasDollarBrace = true;
        }
        j++;
      }

      if (hasDollarBrace && depth === 0) {
        result += '={`' + content.slice(i + 2, j) + '`}';
        i = j + 1;
        continue;
      }
    }

    result += content[i];
    i++;
  }

  return result;
}

function sanitizeContent(raw: string): string {
  return fixMissingTemplateLiterals(decodeHtmlEntities(raw));
}

// ── cleanLlmContent ───────────────────────────────────────────────────────────

/**
 * Strip common LLM output artifacts from file content before writing to disk.
 * - Strips markdown code fences (` ```tsx ... ``` `)
 * - Removes leading stray backslash (Gemini Flash artifact)
 * - Fixes literal `\n` / `\t` / `\r` two-char sequences
 * - Decodes HTML entities + fixes template-literal backticks
 */
export function cleanLlmContent(raw: string): string {
  let content = raw;

  // Strip markdown code fences
  content = content.replace(/^```[\w]*\r?\n?/, '').replace(/\r?\n?```\s*$/, '');

  // Strip leading stray backslash(es) + optional whitespace/newline
  content = content.replace(/^\\+\s*\n/, '');

  content = repairFullyEscapedPayload(content);

  content = sanitizeContent(content);

  return content.trim();
}

// ── containsJsx ───────────────────────────────────────────────────────────────

/**
 * Heuristic: returns true when `src` appears to contain JSX syntax.
 * Used to promote `.ts` → `.tsx` and `.js` → `.jsx` before writing.
 * Conservative — prefers false negatives over false positives.
 */
export function containsJsx(src: string): boolean {
  const jsxOpenTag =
    /(?<![=<])<([A-Z][A-Za-z0-9_]*|div|span|button|input|form|p|h[1-6]|ul|ol|li|nav|header|footer|main|section|article|aside|img|a|table|tr|td|th|thead|tbody|label|select|option|textarea|svg|path|fragment)[\s/>]/;
  return jsxOpenTag.test(src);
}

// ── autoFixImports ────────────────────────────────────────────────────────────
// Ported from chef/app/lib/.server/prototyper/auto-import.ts

const LUCIDE_ICONS: ReadonlySet<string> = new Set([
  'Activity', 'AlertCircle', 'AlertTriangle', 'Archive', 'ArrowDown', 'ArrowDownRight',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowUpRight', 'Award', 'BarChart',
  'BarChart2', 'BarChart3', 'BarChart4', 'Battery', 'Bell', 'BellOff', 'Bike',
  'Bold', 'Bookmark', 'Book', 'BookOpen', 'Box', 'Briefcase', 'Bug', 'Building',
  'Building2', 'Calendar', 'CalendarDays', 'Camera', 'Car', 'Check', 'CheckCircle',
  'CheckCircle2', 'CheckSquare', 'ChevronDown', 'ChevronLeft', 'ChevronRight',
  'ChevronUp', 'ChevronsDown', 'ChevronsLeft', 'ChevronsRight', 'ChevronsUp',
  'ChevronsUpDown', 'Circle', 'CircleAlert', 'CircleCheck', 'CircleX', 'Clipboard',
  'ClipboardCheck', 'Clock', 'Cloud', 'CloudDownload', 'CloudUpload', 'Code',
  'Code2', 'Coffee', 'Cog', 'Columns', 'Command', 'Compass', 'Copy', 'CreditCard',
  'Crown', 'Database', 'Disc', 'DollarSign', 'Dot', 'Download', 'Droplet',
  'Dumbbell', 'Edit', 'Edit2', 'Edit3', 'Eye', 'EyeOff', 'ExternalLink',
  'Facebook', 'Feather', 'File', 'FileCheck', 'FileImage', 'FilePlus', 'FileText',
  'Files', 'Film', 'Filter', 'Flag', 'Flame', 'Flashlight', 'Folder', 'FolderOpen',
  'FolderPlus', 'Forward', 'Frown', 'Gauge', 'Gem', 'Gift', 'Github', 'Globe',
  'Globe2', 'Grid', 'Grid3x3', 'Hash', 'Headphones', 'Heart', 'HeartHandshake',
  'HelpCircle', 'History', 'Home', 'Image', 'Inbox', 'Info', 'Italic', 'Joystick',
  'Key', 'Layers', 'LayoutDashboard', 'LayoutGrid', 'LayoutList', 'LayoutPanelLeft',
  'LayoutTemplate', 'Library', 'LifeBuoy', 'LineChart', 'Link', 'Link2', 'Linkedin',
  'List', 'ListChecks', 'ListOrdered', 'Loader', 'Loader2', 'Lock', 'LogIn',
  'LogOut', 'Mail', 'MailOpen', 'Map', 'MapPin', 'Maximize', 'Maximize2', 'Medal',
  'Megaphone', 'Menu', 'MessageCircle', 'MessageSquare', 'Mic', 'MicOff',
  'Minimize', 'Minimize2', 'Minus', 'MinusCircle', 'Monitor', 'Moon', 'MoreHorizontal',
  'MoreVertical', 'MountainSnow', 'Mountain', 'MousePointer', 'Move', 'Music',
  'Navigation', 'Newspaper', 'Octagon', 'Package', 'Palette', 'Paperclip', 'Pause',
  'PauseCircle', 'Pencil', 'PencilLine', 'PenTool', 'Percent', 'PersonStanding',
  'Phone', 'PhoneCall', 'PhoneOff', 'PieChart', 'Pin', 'PinOff', 'Plane', 'Play',
  'PlayCircle', 'Plus', 'PlusCircle', 'PlusSquare', 'Pocket', 'Power',
  'Presentation', 'Printer', 'Puzzle', 'QrCode', 'RadioReceiver', 'Receipt',
  'RefreshCcw', 'RefreshCw', 'Repeat', 'Reply', 'Rewind', 'Rocket', 'Rss',
  'Save', 'Scale', 'Scissors', 'Search', 'SearchCheck', 'Send', 'Server', 'Settings',
  'Settings2', 'Share', 'Share2', 'Shield', 'ShieldAlert', 'ShieldCheck', 'Ship',
  'ShoppingBag', 'ShoppingCart', 'Shuffle', 'Sidebar', 'Signal', 'Slack',
  'SlidersHorizontal', 'SlidersVertical', 'Smartphone', 'Smile', 'Snowflake',
  'SortAsc', 'SortDesc', 'Speaker', 'Square', 'Star', 'StarOff', 'StickyNote',
  'StopCircle', 'Sun', 'Sunrise', 'Sunset', 'Table', 'Tablet', 'Tag', 'Tags',
  'Target', 'Terminal', 'Tent', 'ThumbsDown', 'ThumbsUp', 'Ticket', 'Timer',
  'ToggleLeft', 'ToggleRight', 'Trash', 'Trash2', 'TrendingDown', 'TrendingUp',
  'Triangle', 'Trophy', 'Truck', 'Tv', 'Twitter', 'Type', 'Underline', 'Undo',
  'Unlock', 'Upload', 'User', 'UserCheck', 'UserCog', 'UserMinus', 'UserPlus',
  'UserX', 'Users', 'Video', 'VideoOff', 'View', 'Volume', 'Volume1', 'Volume2',
  'VolumeX', 'Wallet', 'Watch', 'Waves', 'Webcam', 'Wifi', 'WifiOff', 'Wind',
  'Wrench', 'X', 'XCircle', 'XSquare', 'Youtube', 'Zap', 'ZapOff', 'ZoomIn',
  'ZoomOut',
]);

const SHADCN_COMPONENTS: Readonly<Record<string, string>> = {
  Accordion: 'accordion', AccordionContent: 'accordion', AccordionItem: 'accordion',
  AccordionTrigger: 'accordion',
  Alert: 'alert', AlertDescription: 'alert', AlertTitle: 'alert',
  AlertDialog: 'alert-dialog', AlertDialogAction: 'alert-dialog',
  AlertDialogCancel: 'alert-dialog', AlertDialogContent: 'alert-dialog',
  AlertDialogDescription: 'alert-dialog', AlertDialogFooter: 'alert-dialog',
  AlertDialogHeader: 'alert-dialog', AlertDialogTitle: 'alert-dialog',
  AlertDialogTrigger: 'alert-dialog',
  Avatar: 'avatar', AvatarFallback: 'avatar', AvatarImage: 'avatar',
  Badge: 'badge',
  Button: 'button',
  Calendar: 'calendar',
  Card: 'card', CardContent: 'card', CardDescription: 'card', CardFooter: 'card',
  CardHeader: 'card', CardTitle: 'card',
  ChartContainer: 'chart', ChartTooltip: 'chart', ChartTooltipContent: 'chart',
  ChartLegend: 'chart', ChartLegendContent: 'chart', ChartStyle: 'chart',
  Checkbox: 'checkbox',
  CommandDialog: 'command', CommandEmpty: 'command', CommandGroup: 'command',
  CommandInput: 'command', CommandItem: 'command', CommandList: 'command',
  CommandSeparator: 'command', CommandShortcut: 'command',
  Dialog: 'dialog', DialogClose: 'dialog', DialogContent: 'dialog',
  DialogDescription: 'dialog', DialogFooter: 'dialog', DialogHeader: 'dialog',
  DialogTitle: 'dialog', DialogTrigger: 'dialog',
  DropdownMenu: 'dropdown-menu', DropdownMenuCheckboxItem: 'dropdown-menu',
  DropdownMenuContent: 'dropdown-menu', DropdownMenuGroup: 'dropdown-menu',
  DropdownMenuItem: 'dropdown-menu', DropdownMenuLabel: 'dropdown-menu',
  DropdownMenuPortal: 'dropdown-menu', DropdownMenuRadioGroup: 'dropdown-menu',
  DropdownMenuRadioItem: 'dropdown-menu', DropdownMenuSeparator: 'dropdown-menu',
  DropdownMenuShortcut: 'dropdown-menu', DropdownMenuSub: 'dropdown-menu',
  DropdownMenuSubContent: 'dropdown-menu', DropdownMenuSubTrigger: 'dropdown-menu',
  DropdownMenuTrigger: 'dropdown-menu',
  Input: 'input',
  Label: 'label',
  NavigationMenu: 'navigation-menu', NavigationMenuContent: 'navigation-menu',
  NavigationMenuIndicator: 'navigation-menu', NavigationMenuItem: 'navigation-menu',
  NavigationMenuLink: 'navigation-menu', NavigationMenuList: 'navigation-menu',
  NavigationMenuTrigger: 'navigation-menu', NavigationMenuViewport: 'navigation-menu',
  Popover: 'popover', PopoverContent: 'popover', PopoverTrigger: 'popover',
  Progress: 'progress',
  RadioGroup: 'radio-group', RadioGroupItem: 'radio-group',
  ScrollArea: 'scroll-area', ScrollBar: 'scroll-area',
  Select: 'select', SelectContent: 'select', SelectGroup: 'select',
  SelectItem: 'select', SelectLabel: 'select', SelectScrollDownButton: 'select',
  SelectScrollUpButton: 'select', SelectSeparator: 'select', SelectTrigger: 'select',
  SelectValue: 'select',
  Separator: 'separator',
  Sheet: 'sheet', SheetClose: 'sheet', SheetContent: 'sheet',
  SheetDescription: 'sheet', SheetFooter: 'sheet', SheetHeader: 'sheet',
  SheetTitle: 'sheet', SheetTrigger: 'sheet',
  Skeleton: 'skeleton',
  Slider: 'slider',
  Toaster: 'sonner',
  Switch: 'switch',
  Table: 'table', TableBody: 'table', TableCaption: 'table', TableCell: 'table',
  TableFooter: 'table', TableHead: 'table', TableHeader: 'table', TableRow: 'table',
  Tabs: 'tabs', TabsContent: 'tabs', TabsList: 'tabs', TabsTrigger: 'tabs',
  Textarea: 'textarea',
  Toast: 'toast', ToastAction: 'toast', ToastClose: 'toast',
  ToastDescription: 'toast', ToastProvider: 'toast', ToastTitle: 'toast',
  ToastViewport: 'toast',
  Tooltip: 'tooltip', TooltipContent: 'tooltip', TooltipProvider: 'tooltip',
  TooltipTrigger: 'tooltip',
};

const REACT_EXPORTS: ReadonlySet<string> = new Set([
  'useState', 'useEffect', 'useMemo', 'useCallback', 'useRef',
  'useReducer', 'useContext', 'useLayoutEffect', 'useId',
  'useTransition', 'useDeferredValue', 'useImperativeHandle',
  'useSyncExternalStore', 'useInsertionEffect',
  'createContext', 'forwardRef', 'memo', 'lazy',
]);

const REACT_IMPLICIT: ReadonlySet<string> = new Set([
  'React', 'Fragment', 'Suspense', 'StrictMode', 'Children', 'Component', 'Profiler',
]);

const UTILITY_IDENTIFIERS: Readonly<Record<string, string>> = {
  cn: '@/lib/utils',
  toast: 'sonner',
  cva: 'class-variance-authority',
  twMerge: 'tailwind-merge',
  // The data layer Chef writes into every spec-driven prototype (chef appDataLayer.ts).
  useEntity: '@/lib/appStore',
  useEntityLoading: '@/lib/appStore',
  appAuth: '@/lib/appStore',
};

export interface AutoImportStats {
  added: Array<{ name: string; from: string }>;
  unresolved: string[];
}

export function autoFixImports(rawContent: string): { content: string; stats: AutoImportStats } {
  const stats: AutoImportStats = { added: [], unresolved: [] };

  const sanitized = stripCommentsAndStrings(rawContent);
  const usedTags = extractPascalCaseJsxTags(sanitized);
  const usedHooks = extractKnownReactExports(sanitized);
  const importedNames = collectImportedNames(rawContent);
  const localNames = collectLocalDefinedNames(rawContent);

  const isAlreadyAvailable = (name: string) =>
    importedNames.has(name) || localNames.has(name) || REACT_IMPLICIT.has(name);

  let content = rawContent;

  for (const tag of usedTags) {
    if (isAlreadyAvailable(tag)) continue;
    const source = resolveSourceForJsxTag(tag);
    if (!source) { stats.unresolved.push(tag); continue; }
    content = mergeOrAddNamedImport(content, tag, source);
    importedNames.add(tag);
    stats.added.push({ name: tag, from: source });
  }

  for (const hook of usedHooks) {
    if (isAlreadyAvailable(hook)) continue;
    content = mergeOrAddNamedImport(content, hook, 'react');
    importedNames.add(hook);
    stats.added.push({ name: hook, from: 'react' });
  }

  const usedUtilities = extractKnownUtilityIdentifiers(sanitized);
  for (const util of usedUtilities) {
    if (isAlreadyAvailable(util)) continue;
    const source = UTILITY_IDENTIFIERS[util];
    if (!source) continue;
    content = mergeOrAddNamedImport(content, util, source);
    importedNames.add(util);
    stats.added.push({ name: util, from: source });
  }

  // Defensive: if edits break the import header, discard all changes
  if (stats.added.length > 0 && !isImportHeaderStructurallySound(content)) {
    return { content: rawContent, stats: { added: [], unresolved: stats.unresolved } };
  }

  return { content, stats };
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function resolveSourceForJsxTag(name: string): string | null {
  if (LUCIDE_ICONS.has(name)) return 'lucide-react';
  if (name in SHADCN_COMPONENTS) return `@/components/ui/${SHADCN_COMPONENTS[name]}`;
  return null;
}

function stripCommentsAndStrings(src: string): string {
  return src
    .replace(/\/\*[\s\S]*?\*\//g, (s) => ' '.repeat(s.length))
    .replace(/\/\/.*$/gm, (s) => ' '.repeat(s.length))
    .replace(/'(?:\\.|[^'\\\n])*'/g, (s) => `'${' '.repeat(Math.max(0, s.length - 2))}'`)
    .replace(/"(?:\\.|[^"\\\n])*"/g, (s) => `"${' '.repeat(Math.max(0, s.length - 2))}"`)
    .replace(/`(?:\\.|[^`\\])*`/g, (s) => '`' + ' '.repeat(Math.max(0, s.length - 2)) + '`');
}

function extractPascalCaseJsxTags(src: string): Set<string> {
  const tags = new Set<string>();
  const re = /<([A-Z][A-Za-z0-9_]*)/g;
  for (const m of src.matchAll(re)) tags.add(m[1]);
  return tags;
}

function extractKnownReactExports(src: string): Set<string> {
  const out = new Set<string>();
  for (const name of REACT_EXPORTS) {
    if (new RegExp(`\\b${name}\\b`, 'g').test(src)) out.add(name);
  }
  return out;
}

function extractKnownUtilityIdentifiers(strippedSrc: string): Set<string> {
  const out = new Set<string>();
  for (const name of Object.keys(UTILITY_IDENTIFIERS)) {
    if (new RegExp(`(?<![A-Za-z0-9_$.])${name}(?![A-Za-z0-9_$])`, 'g').test(strippedSrc)) {
      out.add(name);
    }
  }
  return out;
}

function collectImportedNames(src: string): Set<string> {
  const names = new Set<string>();
  const importStmtRe = /import\s+([\s\S]*?)\s+from\s+(['"])[^'"]+\2/g;

  for (const m of src.matchAll(importStmtRe)) {
    const head = m[1];
    const pureTypeImport = /^\s*type\s+\{/.test(head);
    if (pureTypeImport) {
      const bracedMatch = head.match(/\{([^}]+)\}/);
      if (bracedMatch) {
        for (const part of bracedMatch[1].split(',')) {
          const cleaned = part.replace(/^\s*type\s+/, '').trim();
          if (!cleaned) continue;
          const alias = cleaned.match(/\s+as\s+([A-Za-z_$][\w$]*)\s*$/);
          names.add(alias ? alias[1] : cleaned);
        }
      }
      continue;
    }
    const defaultMatch = head.match(/^\s*([A-Za-z_$][\w$]*)\s*(?:,|$)/);
    if (defaultMatch && defaultMatch[1] !== 'type') names.add(defaultMatch[1]);
    const nsMatch = head.match(/\*\s+as\s+([A-Za-z_$][\w$]*)/);
    if (nsMatch) names.add(nsMatch[1]);
    const bracedMatch = head.match(/\{([^}]+)\}/);
    if (bracedMatch) {
      for (const part of bracedMatch[1].split(',')) {
        const cleaned = part.replace(/^\s*type\s+/, '').trim();
        if (!cleaned) continue;
        const alias = cleaned.match(/\s+as\s+([A-Za-z_$][\w$]*)\s*$/);
        names.add(alias ? alias[1] : (cleaned.split(/\s+/)[0] ?? ''));
      }
    }
  }
  return names;
}

function collectLocalDefinedNames(src: string): Set<string> {
  const names = new Set<string>();
  for (const m of src.matchAll(
    /\b(?:const|let|var|function|class|type|interface|enum)\s+([A-Z][\w$]*)/g,
  )) {
    names.add(m[1]);
  }
  return names;
}

function mergeOrAddNamedImport(src: string, name: string, source: string): string {
  const escaped = source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(
    `import\\s+(?!type\\s+)\\{([^}]*)\\}\\s+from\\s+(['"])${escaped}\\2`,
    'm',
  );
  const m = re.exec(src);

  if (m) {
    const fullMatchStart = m.index;
    const openBrace = src.indexOf('{', fullMatchStart);
    const closeBrace = src.indexOf('}', openBrace);
    const inner = src.slice(openBrace + 1, closeBrace).trim();
    if (inner === '') {
      return src.slice(0, openBrace + 1) + ` ${name} ` + src.slice(closeBrace);
    }
    const before = src.slice(0, closeBrace).trimEnd();
    const sep = before.endsWith(',') ? ' ' : ', ';
    return before + sep + name + ' ' + src.slice(closeBrace);
  }

  const lines = src.split('\n');
  const insertIdx = findInsertLineAfterLastImport(lines);
  lines.splice(insertIdx, 0, `import { ${name} } from '${source}';`);
  return lines.join('\n');
}

function findInsertLineAfterLastImport(lines: string[]): number {
  let lastImportEndExclusive = 0;
  let inImport = false;
  let braceDepth = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const trimmed = line.trim();

    if (!inImport) {
      if (trimmed === '' || trimmed.startsWith('//') || trimmed.startsWith('/*') || trimmed.startsWith('*')) {
        continue;
      }
      if (trimmed.startsWith('import ') || trimmed.startsWith('import{')) {
        inImport = true;
        braceDepth = countBraces(line);
        const hasFrom = /\bfrom\s+['"]/.test(line);
        const isSideEffect = /^import\s+['"][^'"]+['"]\s*;?\s*$/.test(trimmed);
        if (isSideEffect || (hasFrom && braceDepth === 0)) {
          lastImportEndExclusive = i + 1;
          inImport = false;
          braceDepth = 0;
        }
        continue;
      }
      break;
    }

    braceDepth += countBraces(line);
    if (braceDepth <= 0 && /\bfrom\s+['"]/.test(line)) {
      lastImportEndExclusive = i + 1;
      inImport = false;
      braceDepth = 0;
    }
  }

  return lastImportEndExclusive;
}

function isImportHeaderStructurallySound(src: string): boolean {
  const lines = src.split('\n');
  let braceDepth = 0;
  let inImport = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!inImport) {
      if (trimmed === '' || trimmed.startsWith('//') || trimmed.startsWith('/*') || trimmed.startsWith('*')) continue;
      if (trimmed.startsWith('import ') || trimmed.startsWith('import{')) {
        inImport = true;
        braceDepth = countBraces(line);
        if (braceDepth < 0) return false;
        const hasFrom = /\bfrom\s+['"]/.test(line);
        const isSideEffect = /^import\s+['"][^'"]+['"]\s*;?\s*$/.test(trimmed);
        if (isSideEffect || (hasFrom && braceDepth === 0)) { inImport = false; braceDepth = 0; }
        continue;
      }
      return braceDepth === 0;
    }
    braceDepth += countBraces(line);
    if (braceDepth < 0) return false;
    if (braceDepth === 0 && /\bfrom\s+['"]/.test(line)) { inImport = false; }
  }

  return braceDepth === 0 && !inImport;
}

function countBraces(line: string): number {
  let net = 0;
  for (const c of line) {
    if (c === '{') net++;
    else if (c === '}') net--;
  }
  return net;
}
